/**
 * Unit tab · Appliances. Make, model and serial for anything that plugs in: photo cards, search, filter by room.
 */
import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Appliance } from "@/hooks/useAppliances";

function warrantyText(a: Appliance): string {
  if (!a.warranty_expiry) return "No warranty";
  try {
    const d = new Date(a.warranty_expiry + "T12:00:00");
    return `${d.getTime() < Date.now() ? "Warranty ended" : "Warranty to"} ${format(d, "MMM yyyy")}`;
  } catch {
    return "No warranty";
  }
}

export function UnitAppliancesPage({
  appliances,
  roomNames,
  onAdd,
  onEdit,
}: {
  appliances: Appliance[];
  roomNames: string[];
  onAdd: () => void;
  onEdit: (a: Appliance) => void;
}) {
  const [q, setQ] = useState("");
  const [room, setRoom] = useState<string>("all");

  /** The room an appliance is in: the room whose name appears in its location. */
  const roomOf = (a: Appliance): string => {
    const loc = (a.location ?? "").toLowerCase();
    return roomNames.find((r) => loc.includes(r.toLowerCase())) ?? "Other";
  };
  const groups = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of appliances) m.set(roomOf(a), (m.get(roomOf(a)) ?? 0) + 1);
    return [...m.entries()].sort((x, y) => y[1] - x[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliances, roomNames]);
  const shown = appliances.filter((a) => {
    if (room !== "all" && roomOf(a) !== room) return false;
    const s = q.trim().toLowerCase();
    return !s || [a.name, a.model_number, a.make, a.serial_number, a.location].some((v) => (v ?? "").toLowerCase().includes(s));
  });

  if (appliances.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-[24px] bg-white px-6 py-16 text-center">
        <div className="text-[22px] font-bold tracking-[-0.3px] text-[#0f1720]">Everything that plugs in</div>
        <div className="max-w-[420px] text-[15px] font-medium text-[#64748b]">Snap the model sticker. We fill in make, model and serial.</div>
        <button type="button" onClick={onAdd} className="mt-2 flex h-12 items-center rounded-[14px] bg-[#15776d] px-6 text-[15px] font-bold text-white">
          Add appliance
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[22px] font-bold tracking-[-0.3px] text-[#0f1720]">Appliances</div>
          <div className="text-[14px] font-medium text-[#64748b]">Make, model and serial for anything that plugs in.</div>
        </div>
        <button type="button" onClick={onAdd} className="flex h-11 shrink-0 items-center rounded-[14px] bg-[#15776d] px-4 text-[14.5px] font-bold text-white">
          Add appliance
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <label className="flex h-11 min-w-[220px] flex-1 items-center gap-2 rounded-[14px] bg-white px-3.5 text-[#8494a2]">
          <Search className="h-4 w-4" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search model or name"
            aria-label="Search appliances"
            className="min-w-0 flex-1 bg-transparent text-[15px] font-medium text-[#0f1720] outline-none placeholder:text-[#a2b0b9]"
          />
        </label>
        <div className="flex flex-wrap gap-2">
          {[["all", "All", appliances.length] as const, ...groups.map(([r, n]) => [r, r, n] as const)].map(([id, label, n]) => (
            <button
              key={id}
              type="button"
              onClick={() => setRoom(id)}
              className={cn(
                "flex h-10 items-center gap-1.5 rounded-full px-3.5 text-[13.5px] font-bold",
                room === id ? "bg-[#0f1720] text-white" : "bg-white text-[#475569] hover:bg-[#eef3f5]",
              )}
            >
              {label}
              <span className={cn("text-[12.5px]", room === id ? "text-white/70" : "text-[#8494a2]")}>{n}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {shown.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onEdit(a)}
            className="flex gap-3.5 rounded-[22px] bg-white p-3.5 text-left hover:shadow-[0_6px_20px_rgba(15,23,32,0.08)]"
          >
            <div className="h-[84px] w-[84px] shrink-0 overflow-hidden rounded-[16px] bg-[#eef2f5]">
              {a.photo_urls?.[0] ? <img src={a.photo_urls[0]} alt="" className="h-full w-full object-cover" /> : null}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[17px] font-bold tracking-[-0.2px] text-[#0f1720]">{a.name}</div>
              <div className="truncate text-[13px] font-medium text-[#8494a2]">{a.location || "No location"}</div>
              <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[13px]">
                <dt className="font-medium text-[#8494a2]">Make</dt>
                <dd className="truncate font-bold text-[#0f1720]">{a.make || "—"}</dd>
                <dt className="font-medium text-[#8494a2]">Model</dt>
                <dd className="truncate font-bold text-[#0f1720]">{a.model_number || "—"}</dd>
                <dt className="font-medium text-[#8494a2]">Serial</dt>
                <dd className="truncate font-bold text-[#0f1720]">{a.serial_number || "—"}</dd>
              </dl>
              <div className={cn("mt-2 text-[12.5px] font-bold", a.warranty_expiry ? "text-[#15776d]" : "text-[#8494a2]")}>{warrantyText(a)}</div>
            </div>
          </button>
        ))}
        {shown.length === 0 ? <div className="col-span-full rounded-[18px] bg-white px-4 py-8 text-center text-[14px] font-medium text-[#8494a2]">No appliances match.</div> : null}
      </div>
    </div>
  );
}
